import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { stockReconciliationService } from '../../services/stockReconciliationService';
import {
  StockReconciliationDTO,
  StockReconciliationApprovalStatusDTO,
  StockReconciliationApprovalDTO,
  StockReconciliationStatus,
} from '../../types';
import './StockReconciliationPage.css';


import { getServerErrorMessage } from '../../utils/errorUtils';
// ─── Approval level row ───────────────────────────────────────────────────────

interface ApprovalLevelRowProps {
  approval: StockReconciliationApprovalDTO;
  currentUserId: number;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
}

const ApprovalLevelRow: React.FC<ApprovalLevelRowProps> = ({
  approval, currentUserId, onApprove, onReject,
}) => {
  const { t } = useTranslation();
  const isMine = approval.assignedToUserId === currentUserId && approval.status === 'Pending';

  const iconClass =
    approval.status === 'Approved' ? 'sr-level-icon--approved' :
    approval.status === 'Rejected' ? 'sr-level-icon--rejected' : 'sr-level-icon--pending';

  const icon =
    approval.status === 'Approved' ? '✓' :
    approval.status === 'Rejected' ? '✗' : approval.approvalLevel.toString();

  return (
    <div className={`sr-level-row ${isMine ? 'sr-level-row--mine' : ''}`}>
      <div className={`sr-level-icon ${iconClass}`}>{icon}</div>
      <div className="sr-level-info">
        <div className="sr-level-title">
          {t('stockReconciliation.approvalLevel')} {approval.approvalLevel}
          {isMine && (
            <span className="sr-level-mine-badge" style={{ marginInlineStart: 8 }}>
              {t('stockReconciliation.yourApproval')}
            </span>
          )}
        </div>
        <div className="sr-level-sub">
          {approval.status === 'Approved' && (
            <span>
              ✓ {t('stockReconciliation.approvedBy')}: {approval.approvingUserName ?? approval.approvingUserId}
              {approval.approvedAt && ` · ${new Date(approval.approvedAt).toLocaleDateString()}`}
              {approval.approvalComments && ` · "${approval.approvalComments}"`}
            </span>
          )}
          {approval.status === 'Rejected' && (
            <span style={{ color: '#991b1b' }}>
              ✗ {t('stockReconciliation.rejectedBy')}: {approval.approvingUserName}
              {approval.rejectionReason && ` · ${approval.rejectionReason}`}
            </span>
          )}
          {approval.status === 'Pending' && (
            <span>
              {t('stockReconciliation.assignedTo')}: {approval.assignedToUserName ?? approval.assignedToUserId}
            </span>
          )}
        </div>
      </div>
      {isMine && (
        <div className="sr-level-actions">
          <button
            className="ims-action-btn ims-action-btn--approve"
            onClick={() => onApprove(approval.id)}
          >
            {t('common.approve')}
          </button>
          <button
            className="ims-action-btn ims-action-btn--reject"
            onClick={() => onReject(approval.id)}
          >
            {t('common.reject')}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const StockReconciliationDetailPage: React.FC = () => {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const { t }     = useTranslation();
  const { user }  = useAuth();

  const STATUS_LABEL: Record<StockReconciliationStatus, string> = {
    Draft:           t('statuses.draft'),
    PendingApproval: t('statuses.pendingApproval'),
    Approved:        t('statuses.approved'),
    Rejected:        t('statuses.rejected'),
    Posted:          t('statuses.posted'),
    Cancelled:       t('statuses.cancelled'),
  };

  const STATUS_CLASS: Record<StockReconciliationStatus, string> = {
    Draft:           'ims-badge sr-badge-draft',
    PendingApproval: 'ims-badge sr-badge-pending',
    Approved:        'ims-badge sr-badge-approved',
    Rejected:        'ims-badge sr-badge-rejected',
    Posted:          'ims-badge sr-badge-posted',
    Cancelled:       'ims-badge sr-badge-cancelled',
  };

  const [reconciliation, setReconciliation]     = useState<StockReconciliationDTO | null>(null);
  const [approvalStatus,  setApprovalStatus]    = useState<StockReconciliationApprovalStatusDTO | null>(null);
  const [loading,        setLoading]            = useState(true);
  const [actionLoading,  setActionLoading]      = useState(false);
  const [error,          setError]              = useState<string | null>(null);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal,  setShowRejectModal]  = useState(false);
  const [pendingApprovalId, setPendingApprovalId] = useState<number | null>(null);
  const [approveComments,  setApproveComments]  = useState('');
  const [rejectReason,     setRejectReason]     = useState('');

  const userId = user?.id ?? 0;

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [rRes, aRes] = await Promise.all([
        stockReconciliationService.getById(id),
        stockReconciliationService.getApprovalStatus(id),
      ]);
      const rec = rRes.data?.data;
      if (rec) setReconciliation(rec);
      const apr = aRes.data?.data;
      if (apr) setApprovalStatus(apr);
    } catch {
      setError(t('stockReconciliation.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const myPendingApproval = approvalStatus?.approvals.find(
    (a) => a.assignedToUserId === userId && a.status === 'Pending'
  ) ?? null;

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!id) return;
    setActionLoading(true);
    setError(null);
    try {
      await stockReconciliationService.submit(id, userId);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('stockReconciliation.failedToSubmit'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveOpen = (approvalId: number) => {
    setPendingApprovalId(approvalId);
    setApproveComments('');
    setShowApproveModal(true);
  };

  const handleRejectOpen = (approvalId: number) => {
    setPendingApprovalId(approvalId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!pendingApprovalId) return;
    setActionLoading(true);
    try {
      await stockReconciliationService.approve(pendingApprovalId, userId, approveComments || undefined);
      setShowApproveModal(false);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingApprovalId || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await stockReconciliationService.reject(pendingApprovalId, userId, rejectReason);
      setShowRejectModal(false);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePost = async () => {
    if (!id) return;
    const confirmed = window.confirm(t('stockReconciliation.confirmPost'));
    if (!confirmed) return;
    setActionLoading(true);
    setError(null);
    try {
      await stockReconciliationService.post(id, userId);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('stockReconciliation.failedToPost'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    const reason = window.prompt(t('stockReconciliation.confirmCancelPrompt'));
    if (reason === null) return; // user clicked Cancel on prompt
    setActionLoading(true);
    setError(null);
    try {
      await stockReconciliationService.cancel(id, userId, reason || undefined);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="ims-page sr-page">
          <div className="ims-loading">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  if (error && !reconciliation) {
    return (
      <Layout>
        <div className="ims-page sr-page">
          <div className="ims-alert ims-alert-error">{error}</div>
        </div>
      </Layout>
    );
  }

  if (!reconciliation) {
    return (
      <Layout>
        <div className="ims-page sr-page">
          <div className="ims-alert ims-alert-error">{t('stockReconciliation.notFound')}</div>
        </div>
      </Layout>
    );
  }

  const status     = reconciliation.status as StockReconciliationStatus;
  const canEdit    = status === 'Draft';
  const canSubmit  = status === 'Draft' || status === 'Rejected';
  const canApprove = status === 'PendingApproval' && !!myPendingApproval;
  const canPost    = status === 'Approved';
  const canCancel  = ['Draft', 'PendingApproval', 'Approved', 'Rejected'].includes(status);

  const steps = [
    { label: t('stockReconciliation.stepDraft'),    done: true },
    { label: t('stockReconciliation.stepApproval'), done: ['Approved', 'Posted'].includes(status) },
    { label: t('stockReconciliation.stepPosted'),   done: status === 'Posted' },
  ];

  // Variance computation
  const itemsWithVariance = reconciliation.items.filter((i) => i.varianceQuantity !== 0);
  const totalPositive = reconciliation.items
    .filter((i) => i.varianceQuantity > 0)
    .reduce((sum, i) => sum + i.varianceQuantity, 0);
  const totalNegative = reconciliation.items
    .filter((i) => i.varianceQuantity < 0)
    .reduce((sum, i) => sum + i.varianceQuantity, 0);

  return (
    <Layout>
      <div className="ims-detail-page sr-page">
        {/* Hero */}
        <div className="ims-hero sr-hero">
          <div className="ims-hero-nav">
            <button
              className="ims-hero-back"
              onClick={() => navigate('/stock-reconciliations')}
            >
              ← {t('common.back')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{reconciliation.reconciliationNumber}</h1>
              <p className="ims-hero-supplier">{reconciliation.warehouseName}</p>
              <div className="ims-hero-badges">
                <span className={STATUS_CLASS[status] || 'ims-badge'}>
                  {STATUS_LABEL[status] ?? status}
                </span>
                <span className="ims-badge" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                  {reconciliation.reconciliationType}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <span className="ims-hero-date">
                {new Date(reconciliation.reconciliationDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="ims-steps">
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              <div className={`ims-step ${step.done ? 'ims-step--done' : ''}`}>
                <div className="ims-step-dot">{step.done ? '✓' : i + 1}</div>
                {step.label}
              </div>
              {i < steps.length - 1 && <span className="ims-step-arrow">›</span>}
            </React.Fragment>
          ))}
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ margin: '0 20px 8px' }}>
            {error}
          </div>
        )}

        {/* Body */}
        <div className="ims-detail-body">
          {/* Main */}
          <div className="ims-detail-main">

            {/* Reconciliation Info */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('stockReconciliation.reconciliationDetails')}</span>
              </div>
              <div className="ims-info-grid">
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('stockReconciliation.warehouse')}</span>
                  <span className="ims-info-value">{reconciliation.warehouseName}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('stockReconciliation.reconciliationType')}</span>
                  <span className="ims-info-value">{reconciliation.reconciliationType}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('stockReconciliation.reconciliationDate')}</span>
                  <span className="ims-info-value">
                    {new Date(reconciliation.reconciliationDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('stockReconciliation.countedBy')}</span>
                  <span className="ims-info-value">{reconciliation.countedByName}</span>
                </div>
                {reconciliation.postedByName && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('stockReconciliation.postedBy')}</span>
                    <span className="ims-info-value">
                      {reconciliation.postedByName}
                      {reconciliation.postedAt && ` · ${new Date(reconciliation.postedAt).toLocaleDateString()}`}
                    </span>
                  </div>
                )}
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('stockReconciliation.approvalStatus')}</span>
                  <span className="ims-info-value">{reconciliation.approvalStatus}</span>
                </div>
                {reconciliation.notes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('common.notes')}</span>
                    <span className="ims-info-value">{reconciliation.notes}</span>
                  </div>
                )}
                {reconciliation.rejectionNotes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('stockReconciliation.rejectionNotes')}</span>
                    <span className="ims-info-value" style={{ color: '#991b1b' }}>
                      {reconciliation.rejectionNotes}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Variance Summary */}
            {itemsWithVariance.length > 0 && (
              <div className="ims-card" style={{ marginTop: 16 }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">{t('stockReconciliation.varianceSummary')}</span>
                </div>
                <div className="ims-card-body">
                  <div className="sr-variance-summary">
                    <div className="sr-variance-stat sr-variance-stat--total">
                      <span className="sr-variance-stat-value">{itemsWithVariance.length}</span>
                      <span className="sr-variance-stat-label">{t('stockReconciliation.itemsWithVariance')}</span>
                    </div>
                    <div className="sr-variance-stat sr-variance-stat--gain">
                      <span className="sr-variance-stat-value">+{totalPositive.toFixed(2)}</span>
                      <span className="sr-variance-stat-label">{t('stockReconciliation.totalGain')}</span>
                    </div>
                    <div className="sr-variance-stat sr-variance-stat--loss">
                      <span className="sr-variance-stat-value">{totalNegative.toFixed(2)}</span>
                      <span className="sr-variance-stat-label">{t('stockReconciliation.totalLoss')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Items Table */}
            <div className="ims-card" style={{ marginTop: 16 }}>
              <div className="ims-card-header">
                <span className="ims-card-title">{t('stockReconciliation.items')}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--ims-text-muted)' }}>
                  {reconciliation.items.length} {t('stockReconciliation.itemsTotal')}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ims-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('stockReconciliation.product')}</th>
                      <th>{t('stockReconciliation.unit')}</th>
                      <th style={{ textAlign: 'right' }}>{t('stockReconciliation.systemQuantity')}</th>
                      <th style={{ textAlign: 'right' }}>{t('stockReconciliation.physicalQuantity')}</th>
                      <th style={{ textAlign: 'right' }}>{t('stockReconciliation.variance')}</th>
                      <th>{t('stockReconciliation.varianceReason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliation.items.map((item, idx) => (
                      <tr key={item.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{item.productName}</div>
                          {item.productCode && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--ims-text-muted)' }}>
                              {item.productCode}
                            </div>
                          )}
                        </td>
                        <td>{item.unitName}</td>
                        <td style={{ textAlign: 'right' }}>{item.systemQuantity}</td>
                        <td style={{ textAlign: 'right' }}>{item.physicalQuantity}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span className={
                            item.varianceQuantity > 0 ? 'variance-positive' :
                            item.varianceQuantity < 0 ? 'variance-negative' : 'variance-zero'
                          }>
                            {item.varianceQuantity > 0 ? '+' : ''}{item.varianceQuantity}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--ims-text-sub)' }}>
                          {item.varianceReason || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Approval Levels */}
            {approvalStatus && approvalStatus.approvals.length > 0 && (
              <div className="ims-card" style={{ marginTop: 16 }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">{t('stockReconciliation.approvalLevels')}</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--ims-text-muted)' }}>
                    {approvalStatus.currentApprovalLevel}/{approvalStatus.totalApprovalLevels}{' '}
                    {t('stockReconciliation.levelsCompleted')}
                  </span>
                </div>
                <div className="ims-card-body">
                  {myPendingApproval && (
                    <div className="sr-pending-banner">
                      ⏳ {t('stockReconciliation.pendingYourApproval')}
                    </div>
                  )}
                  <div className="sr-levels-list">
                    {approvalStatus.approvals.map((a) => (
                      <ApprovalLevelRow
                        key={a.id}
                        approval={a}
                        currentUserId={userId}
                        onApprove={handleApproveOpen}
                        onReject={handleRejectOpen}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="ims-detail-sidebar">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('common.actions')}</span>
              </div>
              <div className="ims-card-body">
                <div className="ims-actions-list">
                  {canEdit && (
                    <button
                      className="ims-action-btn ims-action-btn--edit"
                      onClick={() => navigate(`/stock-reconciliations/${id}/edit`)}
                    >
                      ✏️ {t('common.edit')}
                    </button>
                  )}
                  {canSubmit && (
                    <button
                      className="ims-action-btn ims-action-btn--approve"
                      onClick={handleSubmit}
                      disabled={actionLoading}
                    >
                      📤 {t('stockReconciliation.submitForApproval')}
                    </button>
                  )}
                  {status === 'PendingApproval' && !myPendingApproval && (
                    <div
                      className="ims-action-btn"
                      style={{ background: '#f3f4f6', color: '#4b5563', cursor: 'default' }}
                    >
                      ⏳ {t('stockReconciliation.awaitingApproval')}
                    </div>
                  )}
                  {canApprove && myPendingApproval && (
                    <>
                      <button
                        className="ims-action-btn ims-action-btn--approve"
                        onClick={() => handleApproveOpen(myPendingApproval.id)}
                        disabled={actionLoading}
                      >
                        ✓ {t('common.approve')}
                      </button>
                      <button
                        className="ims-action-btn ims-action-btn--reject"
                        onClick={() => handleRejectOpen(myPendingApproval.id)}
                        disabled={actionLoading}
                      >
                        ✗ {t('common.reject')}
                      </button>
                    </>
                  )}
                  {canPost && (
                    <button
                      className="ims-action-btn ims-action-btn--post"
                      onClick={handlePost}
                      disabled={actionLoading}
                    >
                      📌 {t('stockReconciliation.post')}
                    </button>
                  )}
                  {canCancel && (
                    <button
                      className="ims-action-btn ims-action-btn--reject"
                      onClick={handleCancel}
                      disabled={actionLoading}
                    >
                      🚫 {t('stockReconciliation.cancel')}
                    </button>
                  )}
                  <button
                    className="ims-action-btn ims-action-btn--edit"
                    onClick={() => navigate('/stock-reconciliations')}
                  >
                    ← {t('common.back')}
                  </button>
                </div>
              </div>
            </div>

            {/* Quick stats sidebar card */}
            <div className="ims-card" style={{ marginTop: 16 }}>
              <div className="ims-card-header">
                <span className="ims-card-title">{t('stockReconciliation.summary')}</span>
              </div>
              <div className="ims-card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('stockReconciliation.totalItems')}</span>
                    <span style={{ fontWeight: 600 }}>{reconciliation.items.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('stockReconciliation.itemsWithVariance')}</span>
                    <span style={{ fontWeight: 600, color: itemsWithVariance.length > 0 ? '#991b1b' : 'var(--ims-text-main)' }}>
                      {itemsWithVariance.length}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('stockReconciliation.approvalProgress')}</span>
                    <span style={{ fontWeight: 600 }}>
                      {reconciliation.currentApprovalLevel}/{reconciliation.totalApprovalLevels}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="ims-modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="ims-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('stockReconciliation.approveTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowApproveModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">
                  {t('stockReconciliation.approvalComments')} ({t('common.optional')})
                </label>
                <textarea
                  className="ims-form-control"
                  rows={3}
                  value={approveComments}
                  onChange={(e) => setApproveComments(e.target.value)}
                  placeholder={t('stockReconciliation.approvalCommentsPlaceholder')}
                />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowApproveModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="ims-btn ims-btn-success"
                onClick={handleApproveConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? '…' : t('common.approve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="ims-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="ims-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('stockReconciliation.rejectTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">
                  {t('stockReconciliation.rejectionReason')} <span className="required">*</span>
                </label>
                <textarea
                  className="ims-form-control"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('stockReconciliation.rejectionReasonPlaceholder')}
                />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-delete"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || actionLoading}
              >
                {actionLoading ? '…' : t('common.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StockReconciliationDetailPage;
